import type { Dispatch, RefObject, SetStateAction } from 'react'
import type { JsonValue } from 'type-fest'
import { useCurrency } from 'src/utils/currency'
import { useExtraPackages } from '../api/useExtraPackages'
import { CreditBonusExplanationHelp } from '../components/CreditBonusExplanationHelp'
import { MembershipBoostSwitcher } from '../components/MembershipBoostSwitcher'
import { classifyPacks } from '../domain/classifyPacks'
import { usePackPromotionOption } from '../hooks/usePackPromotionOption'
import { usePackPurchaseFlow } from '../hooks/usePackPurchaseFlow'
import { useSelectedTier } from '../hooks/useSelectedTier'
import { MonthlyPackSection } from './MonthlyPackSection'
import { OneTimePackSection } from './OneTimePackSection'
import { RegularPackSection } from './RegularPackSection'

export interface ExtraPackageListProps {
  afterSuccess?: () => void
  trackingArgs?: Record<string, JsonValue>
  packListStyleProps?: { className?: string }
  setMode: Dispatch<SetStateAction<'plans' | 'packs'>>
  hideOneTimePurchase?: boolean
  onRequireUpgradeClick?: () => void
  membershipPerksRef?: RefObject<HTMLDivElement | null>
}

/**
 * Top-level container for the credit-pack listing — replaces the legacy
 * `ExtraPackItems` component in `components/Membership/MembershipItems.tsx`.
 *
 * Composition:
 *   useSelectedTier        : preview-tier UI state
 *   usePackPromotionOption : coupon-aware promotion option (or skipToken)
 *   useExtraPackages       : fetch + adapt
 *   classifyPacks          : split into one-time / monthly / regular
 *   usePackPurchaseFlow    : auth + checkout + success animation + refetch
 *   <Section> components   : render each bucket
 */
export function ExtraPackageList(props: ExtraPackageListProps) {
  const { selectedTier, setSelectedTier, currentTier, actualTier } =
    useSelectedTier()
  const { promotionOption, shouldWaitForCoupons } = usePackPromotionOption()
  const packages = useExtraPackages({
    currentTier: shouldWaitForCoupons ? undefined : currentTier,
    promotionOption,
  })
  const { submit, showingSuccess } = usePackPurchaseFlow({
    promotionOption,
    trackingArgs: props.trackingArgs,
    afterSuccess: props.afterSuccess,
  })
  const [currency] = useCurrency()

  if (!packages) return null

  const { oneTime, monthly, regular } = classifyPacks(packages, currency, {
    hideOneTimePurchase: props.hideOneTimePurchase,
  })

  const hasOneTime = oneTime.length > 0
  const hasMonthly = monthly.length > 0
  const hasRegular = regular.length > 0

  return (
    <>
      <ul {...props.packListStyleProps}>
        {hasOneTime && (
          <OneTimePackSection
            packs={oneTime}
            showingSuccessId={showingSuccess}
            currentTier={currentTier}
            selectedTier={selectedTier}
            onPurchase={submit}
            onRequireUpgradeClick={props.onRequireUpgradeClick}
          />
        )}
        {hasOneTime && (hasMonthly || hasRegular) && (
          <li className="col-span-full text-lg text-center font-medium uppercase tracking-wide mt-4" />
        )}
        <MonthlyPackSection
          packs={monthly}
          showingSuccessId={showingSuccess}
          currentTier={currentTier}
          selectedTier={selectedTier}
          onPurchase={submit}
          onRequireUpgradeClick={props.onRequireUpgradeClick}
        />
        {hasMonthly && hasRegular && (
          <li className="col-span-full text-lg text-center font-medium uppercase tracking-wide mt-4" />
        )}
        <RegularPackSection
          packs={regular}
          showingSuccessId={showingSuccess}
          currentTier={currentTier}
          selectedTier={selectedTier}
          onPurchase={submit}
          onRequireUpgradeClick={props.onRequireUpgradeClick}
        />
      </ul>
      <div className="w-full flex justify-center my-8 max-sm:mb-12">
        <CreditBonusExplanationHelp className="text-neutral-600 dark:text-neutral-400 font-medium text-sm" />
      </div>

      <div ref={props.membershipPerksRef} className="scroll-m-24">
        {actualTier === 0 && (
          <MembershipBoostSwitcher
            tier={selectedTier}
            onSelect={setSelectedTier}
            setMode={props.setMode}
          />
        )}
      </div>
    </>
  )
}
